/**
 * Authenticated API client for web app
 * Automatically handles Bearer token authentication for mobileApplicationBackend calls
 */

import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import authService from './authService';

// Request configuration interface
interface RequestConfig {
  skipAuth?: boolean;
  skipAuthErrorHandler?: boolean;
}

class AuthenticatedAPIClient {
  private axiosInstance: AxiosInstance;

  constructor() {
    // Create axios instance with default config
    this.axiosInstance = axios.create({
      timeout: 30000, // 30 seconds
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add authentication
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        // Skip auth for certain requests
        if (config.headers?.skipAuth) {
          delete config.headers.skipAuth;
          return config;
        }

        try {
          // Get authentication token
          const token = await authService.getGoogleToken();
          config.headers.Authorization = `Bearer ${token}`;
          
          console.log(`📡 [APIClient] Making authenticated request to: ${config.url}`);
          console.log(`📡 [APIClient] Method: ${config.method?.toUpperCase()}`);
          console.log(`📡 [APIClient] Token length: ${token.length}`);
          console.log(`📡 [APIClient] Token preview: ${token.substring(0, 50)}...`);
        } catch (error: any) {
          console.error('❌ [APIClient] Failed to get auth token:', error);
          console.error('❌ [APIClient] Error details:', error.message);
          
          // If sign-in is required, don't proceed with the request
          if (error.message === 'SIGN_IN_REQUIRED') {
            return Promise.reject(new Error('Authentication required. Please sign in.'));
          }
          
          // For other errors, proceed without auth (might fail at backend)
          console.warn('⚠️ [APIClient] Proceeding without authentication');
        }

        return config;
      },
      (error) => {
        console.error('❌ [APIClient] Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error: AxiosError) => {
        console.log('🔍 [APIClient] Response error:', error.response?.status, error.message);

        // Check if we should skip auth error handling
        if (error.config?.headers?.skipAuthErrorHandler) {
          console.log('🔒 [APIClient] Skipping auth error handler');
          return Promise.reject(error);
        }

        // Handle 403 Forbidden (permission denied) - different from 401
        if (error.response?.status === 403) {
          console.log('🚫 [APIClient] Permission denied error detected');

          // Extract error message from response
          const responseData = error.response?.data as { error?: string; message?: string } | undefined;
          const errorMessage = responseData?.message || responseData?.error || 'You do not have permission to perform this action.';

          // Show user-friendly alert
          alert(`Permission Denied: ${errorMessage}`);

          // Reject with the error (don't retry or refresh token)
          return Promise.reject(new Error(errorMessage));
        }

        // Handle 401 Unauthorized (authentication error)
        if (error.response?.status === 401) {
          console.log('🔒 [APIClient] Authentication error detected');

          // Try to refresh token once
          try {
            console.log('🔄 [APIClient] Attempting token refresh...');
            await authService.refreshTokenIfNeeded();

            // Retry the original request
            const originalRequest = error.config;
            if (originalRequest) {
              const token = await authService.getGoogleToken();
              originalRequest.headers.Authorization = `Bearer ${token}`;

              console.log('🔄 [APIClient] Retrying request with new token');
              return this.axiosInstance(originalRequest);
            }
          } catch (refreshError) {
            console.error('❌ [APIClient] Token refresh failed:', refreshError);

            // Sign out user and show error
            await authService.signOut();

            // Throw user-friendly error
            return Promise.reject(new Error('Your session has expired. Please sign in again.'));
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Make a GET request
   */
  async get<T = any>(url: string, config?: RequestConfig & { params?: any }): Promise<T> {
    const response = await this.axiosInstance.get(url, {
      headers: config?.skipAuth ? { skipAuth: true } : {},
      params: config?.params,
    });
    return response.data;
  }

  /**
   * Make a POST request
   */
  async post<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    const response = await this.axiosInstance.post(url, data, {
      headers: config?.skipAuth ? { skipAuth: true } : {},
    });
    return response.data;
  }

  /**
   * Make a PUT request
   */
  async put<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    const response = await this.axiosInstance.put(url, data, {
      headers: config?.skipAuth ? { skipAuth: true } : {},
    });
    return response.data;
  }

  /**
   * Make a DELETE request
   */
  async delete<T = any>(url: string, config?: RequestConfig): Promise<T> {
    const response = await this.axiosInstance.delete(url, {
      headers: config?.skipAuth ? { skipAuth: true } : {},
    });
    return response.data;
  }

  /**
   * Make a request using the existing axios instance (for backward compatibility)
   */
  async request<T = any>(config: any): Promise<T> {
    const response = await this.axiosInstance(config);
    return response.data;
  }

  /**
   * Get the axios instance for advanced usage
   */
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

// Export singleton instance
export const apiClient = new AuthenticatedAPIClient();
export default apiClient;